import { useEffect } from "react";

/**
 * Dynamic brand / primary colour.
 *
 * The Tailwind `accent` palette is wired to the `--accent-50 … --accent-900`
 * CSS variables (see tailwind.config.ts + index.css). This module turns a
 * single admin-chosen hex colour into a full 50→900 ramp and writes those
 * variables (plus `--primary-color`) onto a target element, so the whole site
 * re-themes at runtime without a rebuild.
 *
 * The default ramp values live in index.css `:root` and match the original
 * forest-green palette exactly, so a missing/blank colour renders identically
 * to before.
 */

/** Original VATÁK forest-green accent — the fallback when none is set. */
export const PRIMARY_COLOR_DEFAULT = "#2f7a3e";

type Rgb = { r: number; g: number; b: number };

/** Tailwind shade → how to derive it from the base (500) colour. */
const RAMP: { shade: number; toward: 0 | 255; t: number }[] = [
  { shade: 50, toward: 255, t: 0.9 },
  { shade: 100, toward: 255, t: 0.79 },
  { shade: 200, toward: 255, t: 0.6 },
  { shade: 300, toward: 255, t: 0.4 },
  { shade: 400, toward: 255, t: 0.2 },
  // 500 is the base colour itself.
  { shade: 600, toward: 0, t: 0.18 },
  { shade: 700, toward: 0, t: 0.35 },
  { shade: 800, toward: 0, t: 0.5 },
  { shade: 900, toward: 0, t: 0.66 },
];

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mix(c: number, toward: number, t: number): number {
  return Math.round(c + (toward - c) * t);
}

/** "r g b" triplet string used by `rgb(var(--accent-500) / <alpha-value>)`. */
function triplet({ r, g, b }: Rgb): string {
  return `${r} ${g} ${b}`;
}

/**
 * Build the full accent ramp from a base hex. Returns a map of CSS variable
 * name → space-separated rgb triplet. Returns null for an invalid colour.
 */
export function accentScaleVars(hex: string): Record<string, string> | null {
  const base = parseHex(hex);
  if (!base) return null;
  const vars: Record<string, string> = { "--accent-500": triplet(base) };
  for (const { shade, toward, t } of RAMP) {
    vars[`--accent-${shade}`] = triplet({
      r: mix(base.r, toward, t),
      g: mix(base.g, toward, t),
      b: mix(base.b, toward, t),
    });
  }
  return vars;
}

/**
 * Apply (or clear) the primary colour on a target element (defaults to
 * <html>). Passing a falsy/invalid colour removes the inline overrides so the
 * static `:root` defaults take over again.
 */
export function applyPrimaryColor(
  hex: string | null | undefined,
  target: HTMLElement | null = typeof document !== "undefined"
    ? document.documentElement
    : null,
): void {
  if (!target) return;
  const vars = hex ? accentScaleVars(hex) : null;
  if (!vars) {
    target.style.removeProperty("--primary-color");
    for (const shade of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]) {
      target.style.removeProperty(`--accent-${shade}`);
    }
    return;
  }
  target.style.setProperty("--primary-color", hex as string);
  for (const [name, value] of Object.entries(vars)) {
    target.style.setProperty(name, value);
  }
}

/**
 * Re-theme <html> whenever the brand primary colour changes. Used by the
 * public layout and the admin shell so the saved colour is reflected app-wide.
 */
export function useApplyPrimaryColor(hex: string | null | undefined): void {
  useEffect(() => {
    applyPrimaryColor(hex ?? PRIMARY_COLOR_DEFAULT);
  }, [hex]);
}
